import startsWith from 'lodash/startsWith';

// service
// import NewRelicService from 'app/client/service/NewRelicService';

const queryUtils = {
  /*
   * Remove multiple instances question marks (?) in query param
   *
   * @param {string} queryParam - the query parameter
   * @returns {string}
   */
  cleanQueryParams(queryParam: string) {
    return queryParam.replace(/\?/g, '&').replace('&', '?');
  },

  /*
   * examples:
   * www.url.com/ -> www.url.com
   * www.url.com/?foo=bar -> www.url.com?foo=bar
   * www.url.com/?foo=bar/ -> www.url.com?foo=bar
   */
  parseExtraSlashes(url: string) {
    const regex = new RegExp('\\/\\?|\\/\\&', 'g');
    let statusCode = 200;

    if (url.match(regex)) {
      url = url.replace(regex, '?');
      statusCode = 301;
    }

    if (url.substr(url.length - 1) === '/') {
      url = url.slice(0, -1);
      statusCode = 301;
    }

    return {
      statusCode,
      url,
    };
  },

  /*
   * Take a path and parse that into path and query params
   *
   * @param {string} the path + query params
   * @returns {obj} {path, queryParams}
   */
  parsePathAndQueryParams(fullPath: string) {
    fullPath = this.cleanQueryParams(fullPath);
    const parts = fullPath.split('?');
    const path = parts[0];
    let queryParams = '';

    if (parts.length > 1) {
      queryParams = this.stringify(this.parse(parts[1]));
    }

    return {
      path,
      queryParams,
    };
  },

  sanitize(obj: { [key: string]: string } = {}): { [key: string]: string } {
    const newQueryObj: { [key: string]: string } = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key]) {
        // remove everything past ?
        if (obj[key].lastIndexOf('?') > -1) {
          obj[key] = obj[key].substr(0, obj[key].lastIndexOf('?'));
        }
        newQueryObj[encodeURIComponent(key)] = encodeURIComponent(obj[key]);
      }
    }

    return newQueryObj;
  },
  stringify(obj: { [key: string]: boolean | string }) {
    const str: Array<string> = [];
    let p;

    for (p in obj) {
      if (obj.hasOwnProperty(p) && (obj[p] || obj[p] === false)) {
        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
      }
    }

    if (str.length > 0) {
      return '?' + str.join('&');
    } else {
      return '';
    }
  },
  parse(string = '') {
    if (string === '') {
      return {};
    }

    const queryObj: { [key: string]: string } = {};
    let pieces: string | Array<string> = string;

    if (startsWith(pieces, '?')) {
      pieces = pieces.split('?')[1];
    }
    pieces = pieces.split('&');

    pieces.forEach(function (piece) {
      const sliceIndex = piece.indexOf('='); //get index of first "="
      const name = piece.slice(0, sliceIndex);
      const value = piece.slice(sliceIndex + 1);

      try {
        queryObj[name] = decodeURIComponent(value);
      } catch (error) {
        // const errorMsg = `ERROR @ queryUtils.parse: ${error}`;
        // NewRelicService.dispatch({
        //     message: errorMsg,
        //     context: 'queryUtils.parse'
        // });
      }
    });

    return queryObj;
  },
  encodeQueryString(string: string) {
    return this.stringify(this.parse(string));
  },

  /*
   * Return path with query parameters we want to persist
   *
   * @param {string} fullPath - The full path including query params
   * @param {array} tempWhiteList - A list to append to the current query param white list
   * @returns {string}
   */
  getPathWithPresistedQueryParams(
    fullPath: string,
    tempWhiteList: Array<string> = [],
    displayDeprecation: boolean = true,
  ) {
    if (displayDeprecation) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Deprecation]: `ZRMUtils.queryUtils.getPathWithPresistedQueryParams` function has been deprecated ' +
          'and will be removed in the next MAJOR release. Use ZRMUtils.queryUtils.getPathWithPersistedQueryParams',
      );
    }

    const locationObj = this.parsePathAndQueryParams(fullPath);
    const { path, queryParams } = locationObj;

    const cleanedQueryParams = this.cleanQueryParams(queryParams);

    // list of query params we want to persist as the user navigates
    const QUERY_PARAM_WHITE_LIST = ['utm_source', 'utm_medium', 'utm_campaign', 'itc'].concat(tempWhiteList);

    const queryParamObj = this.parse(cleanedQueryParams);

    // loop through query params
    for (const queryParam in queryParamObj) {
      // check if query params if in the white list
      if (QUERY_PARAM_WHITE_LIST.indexOf(queryParam) < 0) {
        // did not find match - remove queryParam
        delete queryParamObj[queryParam];
      }
    }

    // separate path and query params as an object
    const queryParamsString = this.stringify(queryParamObj);
    const tempObj = this.parsePathAndQueryParams(`${path}${queryParamsString}`);

    return `${tempObj.path}${tempObj.queryParams}`;
  },

  getPathWithPersistedQueryParams(fullPath: string, tempWhiteList: Array<string> = []) {
    return this.getPathWithPresistedQueryParams(fullPath, tempWhiteList, false);
  },
};

export default queryUtils;
